import type { EcomFooterProps } from '@jouzen/ecom-components/layout';
import {
  LinksCategoryId,
  type LinksData,
} from '@jouzen/ecom-components/v3/data/types';
import { type DynamicConfig, useDynamicConfig } from '@statsig/react-bindings';
import { cloneDeep } from 'lodash';
import { useLocale, useTranslations } from 'next-intl';

import tFooterCardEmValue from '../_utils/tFooterCardEmValue';
import { useLocalizeLinks } from './useLocalizeLinks';

interface FooterLinkProps extends Omit<DynamicConfig, 'value'> {
  value: {
    footerLinks: EcomFooterProps['footerLinks'];
    legalLinks: EcomFooterProps['legalLinks'];
  };
}

const useFooterLinks = (footerLinks: LinksData[] = []): LinksData[] => {
  const locale = useLocale();
  const footerCardIndex = footerLinks.findIndex(
    (card) => card.id === LinksCategoryId.FooterCard,
  );

  let footerCard: LinksData[] | undefined;

  if (footerCardIndex >= 0) {
    footerCard = footerLinks.splice(footerCardIndex, 1);
  }

  // use locale in blog link href
  const blogCategory = footerLinks.find(
    ({ id }) => id === LinksCategoryId.BlogCategory,
  );
  const blogLink = blogCategory?.links?.find(({ id }) => id === 'blog');
  if (blogLink && locale !== 'en') {
    blogLink.href = `${blogLink.href}/${locale}`;
  }

  // translate statsig link fields
  const localizedLinks = useLocalizeLinks(footerLinks);
  const localizedFooterCard = useLocalizeLinks(footerCard, tFooterCardEmValue);

  if (localizedFooterCard.length) {
    // place footer card back into the array
    localizedLinks.splice(footerCardIndex, 0, ...localizedFooterCard);
  }

  return localizedLinks;
};

const useFooterConfig = (): FooterLinkProps['value'] | undefined => {
  const t = useTranslations();
  const config = useDynamicConfig('global_footer_links');

  const footerConfigValue = config.value as FooterLinkProps['value'];
  const activeFooterConfig = Object.keys(footerConfigValue).length > 0;

  const statsigLinks = cloneDeep(footerConfigValue);
  const footerLinks = useFooterLinks(statsigLinks.footerLinks);
  const legalLinks = statsigLinks.legalLinks?.map((link) => ({
    ...link,
    label: t(link.label as string),
  }));

  if (!activeFooterConfig) {
    return undefined;
  }

  return {
    footerLinks,
    legalLinks,
  };
};

export default useFooterConfig;
